import { Children, isValidElement, useCallback, useEffect, useState } from "react";
import GenericTabs, { GenericTabsProps } from "./Tabs";
import { TabItem } from "./TabItem";

type RoutedTabsProps = Omit<GenericTabsProps, 'defaultTab' | 'onChange'> & {
  paramName?: string;
};

const RoutedTabs: React.FC<RoutedTabsProps> = ({
  paramName = 'tab',
  children,
  ...props
}) => {
  const [currentTab, setCurrentTab] = useState<number | string>('');

  // Fonction pour obtenir le paramètre de l'URL
  const getTabFromUrl = useCallback((): string => {
    const params = new URLSearchParams(window.location.search);
    const tabValue = params.get(paramName);

    // Si pas de paramètre dans l'URL, prendre le premier tab
    if (!tabValue) {
      const firstTab = extractFirstTabValue(children);
      return firstTab;
    }

    return tabValue;
  }, [children, paramName]);

  // Extraire la valeur du premier TabItem
  const extractFirstTabValue = (children: React.ReactNode): string => {
    let firstValue = '';
    Children.forEach(children, (child) => {
      if (isValidElement(child) && child.type === TabItem && !firstValue) {
        firstValue = String(child.props.value);
      }
    });
    return firstValue || '0';
  };

  // Fonction pour mettre à jour le paramètre dans l'URL
  const updateUrlParam = useCallback((value: number | string): void => {
    const url = new URL(window.location.href);
    url.searchParams.set(paramName, String(value));
    window.history.pushState({}, '', url.toString());
  }, [paramName]);

  // Synchroniser avec l'URL au chargement
  useEffect(() => {
    const tabFromUrl = getTabFromUrl();
    setCurrentTab(tabFromUrl);
  }, [getTabFromUrl]);

  // Gérer les changements de navigation (boutons précédent/suivant)
  useEffect(() => {
    const handlePopState = () => {
      const newTab = getTabFromUrl();
      setCurrentTab(newTab);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [paramName, getTabFromUrl]);

  // Gérer le changement de tab
  const handleTabChange = (value: number | string) => {
    updateUrlParam(value);
    setCurrentTab(value);
  };

  return (
    <GenericTabs
      {...props}
      defaultTab={currentTab}
      onChange={handleTabChange}
    >
      {children}
    </GenericTabs>
  );
};

export default RoutedTabs
